import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { createCustomAdventure, updateCustomAdventure, getCustomAdventures, type CustomAdventureInput, type DropdownChoice } from '@/utils/businessAppApi';
import { DEFAULT_OPTIONS } from '../constants';
import { type CustomAdventure, type OptionType } from '../types';
import { ensureStringArray, ensureEnhancedArray } from '../utils';

interface UseAdventureFormProps {
  onAdventureCreated?: () => void;
  adventureToEdit?: CustomAdventure | null;
  onEditComplete?: () => void;
}

export function useAdventureForm({ onAdventureCreated, adventureToEdit, onEditComplete }: UseAdventureFormProps) {
  // Form state
  const [adventureName, setAdventureName] = useState('');
  const [adventureDescription, setAdventureDescription] = useState('');
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [newOptionType, setNewOptionType] = useState('text');
  const [basePrice, setBasePrice] = useState(0);
  const [basePriceType, setBasePriceType] = useState<'fixed' | 'per_person' | 'per_day' | 'per_person_day'>('fixed');
  const [currency, setCurrency] = useState('USD');
  const [images, setImages] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingAdventure, setEditingAdventure] = useState<CustomAdventure | null>(null);
  const [hasExistingAdventures, setHasExistingAdventures] = useState(false);
  
  // Simplified state for new interface
  const [coreOptions, setCoreOptions] = useState<any[]>([]);
  const [additionalOptions, setAdditionalOptions] = useState<any[]>([]);

  // Check for existing adventures
  useEffect(() => {
    const checkForExistingAdventures = async () => {
      try {
        const data = await getCustomAdventures();
        setHasExistingAdventures(data.length > 0);
      } catch (err) {
        console.error('Failed to check for existing adventures:', err);
      }
    };
    
    checkForExistingAdventures();
  }, []);

  // Handle editing adventure
  useEffect(() => {
    if (adventureToEdit) {
      setEditingAdventure(adventureToEdit);
      setAdventureName(adventureToEdit.name);
      setAdventureDescription(adventureToEdit.description);
      setOptions(adventureToEdit.options);
      setBasePrice(adventureToEdit.basePrice || 0);
      setBasePriceType(adventureToEdit.basePriceType || 'fixed');
      setCurrency(adventureToEdit.currency || 'USD');
      setImages(adventureToEdit.images || []);
    }
  }, [adventureToEdit]);

  // Form submission
  const handleFinish = async () => {
    setIsSubmitting(true);
    try {
      const adventureData = {
        name: adventureName,
        description: adventureDescription,
        options,
        basePrice,
        basePriceType,
        currency,
        images,
        isActive: true,
        published: editingAdventure?.published || false
      };

      if (editingAdventure?.id) {
        await updateCustomAdventure(editingAdventure.id, adventureData);
        toast.success('Adventure updated successfully!');
        if (onEditComplete) onEditComplete();
      } else {
        await createCustomAdventure(adventureData);
        toast.success('Custom adventure created!');
        if (onAdventureCreated) onAdventureCreated();
      }

      resetForm();
      return true;
    } catch (error: any) {
      console.error('Error in handleFinish:', error);
      toast.error(editingAdventure ? 'Failed to update adventure' : 'Failed to create adventure', {
        description: error?.message || 'Please try again.'
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setAdventureName('');
    setAdventureDescription('');
    setOptions(DEFAULT_OPTIONS);
    setBasePrice(0);
    setBasePriceType('fixed');
    setCurrency('USD');
    setImages([]);
    setEditingAdventure(null);
    setNewOptionLabel('');
    setNewOptionType('text');
    if (onEditComplete) onEditComplete();
  };

  // Option handlers
  const handleRemoveOption = (idx: number) => {
    const option = options[idx];
    if (option.label === 'Number of People' || option.label === 'Number of Days') {
      toast.error('This option is required and cannot be deleted');
      return;
    }
    setOptions(options.filter((_, i) => i !== idx));
  };

  const handleOptionLabelChange = (idx: number, value: string) => {
    setOptions(options.map((opt, i) => i === idx ? { ...opt, label: value } : opt));
  };

  const handleOptionTypeChange = (idx: number, value: string) => {
    setOptions(options.map((opt, i) => i === idx ? { 
      ...opt, 
      type: value, 
      options: value === 'select' ? ['Option 1', 'Option 2'] as string[] : undefined 
    } : opt));
  };

  const handleDropdownOptionChange = (optIdx: number, dropdownIdx: number, value: string) => {
    setOptions(options.map((opt, i) =>
      i === optIdx
        ? { 
            ...opt, 
            options: ensureStringArray(opt.options).map((o, j) => j === dropdownIdx ? value : o) as string[]
          }
        : opt
    ));
  };

  const handleAddDropdownOption = (optIdx: number) => {
    setOptions(options.map((opt, i) =>
      i === optIdx
        ? { 
            ...opt, 
            options: [...ensureStringArray(opt.options), `Option ${ensureStringArray(opt.options).length + 1}`] as string[]
          }
        : opt
    ));
  };

  const handleRemoveDropdownOption = (optIdx: number, dropdownIdx: number) => {
    setOptions(options.map((opt, i) =>
      i === optIdx
        ? { 
            ...opt, 
            options: ensureStringArray(opt.options).filter((_, j) => j !== dropdownIdx) as string[]
          }
        : opt
    ));
  };

  // Pricing handlers
  const handleAddOptionPricing = (optIdx: number) => {
    setOptions(prevOptions => prevOptions.map((opt, i) => 
      i === optIdx 
        ? { 
            ...opt, 
            pricing: [...(opt.pricing || []), { type: 'flat', value: 0, description: '' }] 
          }
        : opt
    ));
  };

  const handleRemoveOptionPricing = (optIdx: number, pricingIdx: number) => {
    setOptions(prevOptions => prevOptions.map((opt, i) => 
      i === optIdx 
        ? { 
            ...opt, 
            pricing: (opt.pricing || []).filter((_, j) => j !== pricingIdx)
          }
        : opt
    ));
  };

  const handlePricingTypeChange = (optIdx: number, pricingIdx: number, value: string) => {
    setOptions(prevOptions => prevOptions.map((opt, i) => 
      i === optIdx 
        ? { 
            ...opt, 
            pricing: (opt.pricing || []).map((p, j) => 
              j === pricingIdx 
                ? { ...p, type: value as 'flat' | 'percentage' | 'per_unit' }
                : p
            )
          }
        : opt
    ));
  };

  const handlePricingValueChange = (optIdx: number, pricingIdx: number, value: number) => {
    setOptions(prevOptions => prevOptions.map((opt, i) => 
      i === optIdx 
        ? { 
            ...opt, 
            pricing: (opt.pricing || []).map((p, j) => 
              j === pricingIdx 
                ? { ...p, value }
                : p
            )
          }
        : opt
    ));
  };

  const handlePricingDescriptionChange = (optIdx: number, pricingIdx: number, description: string) => {
    setOptions(prevOptions => prevOptions.map((opt, i) => 
      i === optIdx 
        ? { 
            ...opt, 
            pricing: (opt.pricing || []).map((p, j) => 
              j === pricingIdx 
                ? { ...p, description }
                : p
            )
          }
        : opt
    ));
  };

  // Enhanced dropdown functionality handlers
  const handleEnhanceDropdownChoice = (optIdx: number, choiceIdx: number) => {
    setOptions(prevOptions => prevOptions.map((opt, i) => 
      i === optIdx 
        ? { 
            ...opt, 
            options: ensureEnhancedArray(opt.options).map((choice, j) => 
              j === choiceIdx 
                ? { ...choice, pricing: [{ type: 'flat' as const, value: 0, description: '', multiplierType: 'people' }] } as DropdownChoice
                : choice
            )
          }
        : opt
    ));
  };

  const handleDropdownChoicePricingAdd = (optIdx: number, choiceIdx: number) => {
    console.log('Pricing add called but simplified system only allows one pricing per choice');
  };

  const handleDropdownChoicePricingRemove = (optIdx: number, choiceIdx: number, pricingIdx: number) => {
    setOptions(prevOptions => prevOptions.map((opt, i) => 
      i === optIdx 
        ? { 
            ...opt, 
            options: ensureEnhancedArray(opt.options).map((choice, j) => 
              j === choiceIdx 
                ? choice.value
                : choice
            ) as string[]
          }
        : opt
    ));
  };

  const handleDropdownChoicePricingChange = (optIdx: number, choiceIdx: number, pricingIdx: number, field: string, value: any) => {
    setOptions(prevOptions => prevOptions.map((opt, i) => 
      i === optIdx 
        ? { 
            ...opt, 
            options: ensureEnhancedArray(opt.options).map((choice, j) => 
              j === choiceIdx 
                ? { 
                    ...choice, 
                    pricing: (choice.pricing || []).map((p, k) => 
                      k === pricingIdx 
                        ? { ...p, [field]: value }
                        : p
                    )
                  }
                : choice
            )
          }
        : opt
    ));
  };

  const handleSelectionModeChange = (optIdx: number, mode: 'single' | 'multiple') => {
    setOptions(prevOptions => prevOptions.map((opt, i) => 
      i === optIdx 
        ? { ...opt, selectionMode: mode }
        : opt
    ));
  };

  const handleMultiplierToggle = (optIdx: number, enabled: boolean) => {
    setOptions(prevOptions => prevOptions.map((opt, i) => 
      i === optIdx 
        ? { ...opt, isMultiplier: enabled, multiplierTarget: enabled ? opt.multiplierTarget : undefined }
        : opt
    ));
  };

  const handleMultiplierTargetChange = (optIdx: number, target: string) => {
    setOptions(prevOptions => prevOptions.map((opt, i) => 
      i === optIdx 
        ? { ...opt, multiplierTarget: target }
        : opt
    ));
  };

  return {
    // State
    adventureName,
    setAdventureName,
    adventureDescription,
    setAdventureDescription,
    options,
    setOptions,
    newOptionLabel,
    setNewOptionLabel,
    newOptionType,
    setNewOptionType,
    basePrice,
    setBasePrice,
    basePriceType,
    setBasePriceType,
    currency,
    setCurrency,
    images,
    setImages,
    isSubmitting,
    editingAdventure,
    hasExistingAdventures,
    
    // Simplified state
    coreOptions,
    setCoreOptions,
    additionalOptions,
    setAdditionalOptions,
    
    // Actions
    handleFinish,
    resetForm,
    
    // Option handlers
    handleRemoveOption,
    handleOptionLabelChange,
    handleOptionTypeChange,
    handleDropdownOptionChange,
    handleAddDropdownOption,
    handleRemoveDropdownOption,
    
    // Pricing handlers
    handleAddOptionPricing,
    handleRemoveOptionPricing,
    handlePricingTypeChange,
    handlePricingValueChange,
    handlePricingDescriptionChange,
    
    // Enhanced dropdown handlers
    handleEnhanceDropdownChoice,
    handleDropdownChoicePricingAdd,
    handleDropdownChoicePricingRemove,
    handleDropdownChoicePricingChange,
    handleSelectionModeChange,
    handleMultiplierToggle,
    handleMultiplierTargetChange,
  };
} 